import { ReactElement, SVGProps, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useForm } from '@tanstack/react-form';
import {
  Button,
  FullLoader,
  IconButton,
  Input,
  Modal,
  ModalFooter,
} from '@sc/shared-ui';
import {
  GQL_MUTATION_SEND_DOMAIN_VERIFICATION_EMAIL,
  GQL_QUERY_READ_DOMAIN,
} from '../utility/queries';
import { notify } from '../utility/notify';
import { useAlerts } from '../providers/alerts';
import env from '../environment';
import { track } from '../utility/analytics';

export interface VerifiableDomain {
  uuid: string;
  url: string;
}

interface Props {
  domain: VerifiableDomain | undefined;
  onClose: () => void;
}

interface DnsRecord {
  type: string;
  name: string;
  value: string;
}

const SpinningArrowPathIcon = (
  props: SVGProps<SVGSVGElement>
): ReactElement => (
  <ArrowPathIcon
    {...props}
    className={classNames(props.className, 'animate-spin')}
  />
);

const StatusIndicator = ({ active }: { active: boolean }): ReactElement => (
  <div className="flex items-center space-x-2">
    <span className="relative flex size-2.5">
      {active && (
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#3eb077] opacity-75 animate-ping" />
      )}
      <span
        className={classNames(
          'relative inline-flex size-2.5 rounded-full',
          active ? 'bg-[#3eb077]' : 'bg-red-400'
        )}
      />
    </span>
    <span className="text-sm font-medium">
      {active ? 'Active' : 'Inactive'}
    </span>
  </div>
);

const CopyableCell = ({
  domainUuid,
  value,
}: {
  domainUuid: string;
  value: string;
}): ReactElement => (
  <div className="flex items-center justify-between space-x-2 px-4 py-3 border-l border-grey-t-700 min-w-0">
    <span className="text-sm break-all">{value}</span>
    <IconButton
      tooltip="Copy"
      size="small"
      type="tertiary"
      Icon={ClipboardDocumentIcon}
      onClick={() =>
        void navigator.clipboard.writeText(value).then(() => {
          notify.success('Copied!');

          track('Domain Record Copied', { domainUuid });
        })
      }
    />
  </div>
);

const DnsRecordsTable = ({
  domainUuid,
  records,
}: {
  domainUuid: string;
  records: DnsRecord[];
}): ReactElement => (
  <div className="border border-grey-t-700 rounded-xs overflow-hidden">
    <div className="grid grid-cols-[80px_1.2fr_1fr] bg-background-primary border-b border-grey-t-700">
      <span className="px-4 py-2 text-xs font-semibold text-text-low-priority text-center">
        Type
      </span>
      <span className="px-4 py-2 text-xs font-semibold text-text-low-priority border-l border-grey-t-700">
        Name
      </span>
      <span className="px-4 py-2 text-xs font-semibold text-text-low-priority border-l border-grey-t-700">
        Value
      </span>
    </div>
    {records.map((record) => (
      <div
        key={`${record.type}:${record.name}`}
        className="grid grid-cols-[80px_1.2fr_1fr] border-b border-grey-t-700 last:border-b-0"
      >
        <span className="px-4 py-3 text-sm flex items-center justify-center">
          {record.type}
        </span>
        <CopyableCell domainUuid={domainUuid} value={record.name} />
        <CopyableCell domainUuid={domainUuid} value={record.value} />
      </div>
    ))}
  </div>
);

export const DomainVerificationModal = ({
  domain,
  onClose,
}: Props): ReactElement => {
  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const { data, refetch } = useQuery(GQL_QUERY_READ_DOMAIN, {
    variables: { uuid: domain?.uuid ?? '' },
    skip: !domain,
  });

  const verificationRecords = data?.readDomain.verificationRecords ?? [];

  const isActive = data?.readDomain.cloudflareStatus === 'active';

  // The hostname ownership record arrives with the domain, but the SSL
  // validation records only appear once Cloudflare has placed the certificate
  // order — Check status refetches until they show up.
  const hasSslRecords = verificationRecords.some(
    (record) => record.purpose === 'ssl'
  );

  const [checking, setChecking] = useState(false);

  const checkStatus = async () => {
    if (!domain) return;

    setChecking(true);

    try {
      const result = await refetch();

      const isNowActive = result.data?.readDomain.cloudflareStatus === 'active';

      track('Domain Status Checked', {
        domainUuid: domain.uuid,
        isActive: isNowActive,
      });

      if (!isNowActive)
        notify.info(
          'Still pending — DNS changes can take a while to propagate.'
        );
    } finally {
      setChecking(false);
    }
  };

  const [sendInstructions, { loading: sending }] = useMutation(
    GQL_MUTATION_SEND_DOMAIN_VERIFICATION_EMAIL
  );

  const sendForm = useForm({
    defaultValues: {
      email: '',
    },
    onSubmit: async ({ value, formApi }) => {
      if (!domain) return;

      const email = value.email.trim();

      return sendInstructions({
        variables: {
          input: {
            uuid: domain.uuid,
            email,
          },
        },
        onCompleted: () => {
          notify.success(`Instructions sent to ${email}`);

          track('Domain Verification Email Sent', { domainUuid: domain.uuid });

          formApi.reset();
        },
        onError,
      });
    },
  });

  useEffect(() => {
    sendForm.reset();
  }, [sendForm, domain]);

  useEffect(() => {
    if (domain)
      track('Domain Instructions Viewed', { domainUuid: domain.uuid });
  }, [domain]);

  const cname = env.chatsUrl.split('https://')[1] ?? 'chat.starkchat.com';

  return (
    <Modal
      open={!!domain}
      onClose={onClose}
      title="Verify domain"
      dialogPanelClassname="w-200"
    >
      {domain &&
        (!verificationRecords.length && !isActive ? (
          <div className="p-5 py-16 flex flex-col items-center justify-center space-y-3">
            <FullLoader className="size-6" />
            <span className="text-sm text-text-low-priority">
              Loading your verification records…
            </span>
          </div>
        ) : (
          <div className="p-5 flex flex-col space-y-5">
            <form
              className='max-w-140'
              onSubmit={(e) => {
                e.preventDefault();

                e.stopPropagation();

                void sendForm.handleSubmit();
              }}
            >
              <sendForm.Field
                name="email"
                validators={{
                  onSubmit: ({ value }) =>
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
                      ? undefined
                      : 'Please enter a valid email address.',
                }}
                children={({ state, handleChange }) => (
                  <Input
                    label="Email these instructions"
                    helperText="Send the records to whoever manages your DNS."
                    placeholder="e.g. it@yourcompany.com"
                    value={state.value}
                    error={state.meta.errors.join(', ')}
                    onChange={(e) => handleChange(e.target.value)}
                    inlineButton={{
                      button: {
                        type: 'secondary',
                        text: 'Send instructions',
                        // Button's own `loading` loader is absolutely
                        // positioned past the button edge and gets clipped
                        // inside the input container, so spin the icon
                        // instead.
                        IconEnd: sending
                          ? SpinningArrowPathIcon
                          : EnvelopeIcon,
                        isSubmit: true,
                        shrink: true,
                        disabled: sending,
                      },
                    }}
                  />
                )}
              />
            </form>
            <hr className='bg-grey-900 h-px border-none' />
            <DnsRecordsTable
              domainUuid={domain.uuid}
              records={[
                { type: 'CNAME', name: domain.url, value: cname },
                ...verificationRecords.map((record) => ({
                  type: 'TXT',
                  name: record.name,
                  value: record.value,
                })),
              ]}
            />
            <div className="flex items-center justify-between">
              <StatusIndicator active={isActive} />
              {!isActive && (
                <Button
                  button={{
                    text: 'Check status',
                    type: 'secondary',
                    size: 'small',
                    shrink: true,
                    disabled: checking,
                    IconEnd: checking ? SpinningArrowPathIcon : ArrowPathIcon,
                    onClick: () => void checkStatus(),
                  }}
                />
              )}
            </div>
            {!hasSslRecords && !isActive && (
              <div className="flex items-center space-x-3">
                <FullLoader className="size-5" />
                <span className="text-sm text-text-low-priority">
                  Generating your SSL validation records — more TXT records are
                  on their way…
                </span>
              </div>
            )}
            <p className="text-text-low-priority text-sm">
              To use your custom domain name you'll need to add the following
              DNS records to <span className="font-medium">{domain.url}</span>.
            </p>
            <p className="text-text-low-priority text-sm">
              The TXT records verify your domain and issue its SSL certificate,
              and the CNAME record routes traffic to your chat. It may take up
              to 24 hours for DNS to update. Once these are added the domain
              name will be ready to use.
            </p>
          </div>
        ))}
      <ModalFooter onClose={onClose} closeText="Close" />
    </Modal>
  );
};
